import { useEffect } from "react";

import { Drawer } from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";
import { useLocation } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { isXsQuery } from "@/components/layout/Media";
import { ChatMessage } from "@/components/pages_logged_in/roleplays/details/scenario/ChatMessage";
import {
  FEEDBACK_MEDIA_QUERY,
  FEEDBACK_NAVIGATION_WIDTH,
  MAX_FEEDBACK_CONTAINER_WIDTH,
} from "@/components/pages_logged_in/roleplays/details/scenario/session/constants";
import { FeedbackNavigationButtons } from "@/components/pages_logged_in/roleplays/details/scenario/session/FeedbackNavigationButtons";
import { RoleplaySessionComments } from "@/components/pages_logged_in/roleplays/details/scenario/session/RoleplaySessionComments";
import { RoleplaySessionDetailFeedback } from "@/components/pages_logged_in/roleplays/details/scenario/session/RoleplaySessionDetailFeedback";
import {
  LIGHT_GRAY,
  SIDE_NAV_WIDTH,
  STANDARD_NAV_HEIGHT,
} from "@/css/constants";
import { RoleplaySessionTranscriptFragment } from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";

interface RoleplayFeedbacksProps {
  selectedMessage: RoleplaySessionTranscriptFragment;
  onClose: () => void;
  onNavigate: (direction: "up" | "down") => void;
}

function DrawerFeedback({
  selectedMessage,
  onClose,
  onNavigate,
}: RoleplayFeedbacksProps) {
  const isXs = useMediaQuery(isXsQuery);
  const aiComments = ensureNonNullArray(selectedMessage.aiComments);

  const drawerStyle = isXs
    ? {
        overflow: "auto",
        width: "100%",
        height: "100%",
        backgroundColor: "white",
      }
    : {
        overflow: "auto",
        width: "100%",
        height: "100%",
        maxWidth: `${MAX_FEEDBACK_CONTAINER_WIDTH + FEEDBACK_NAVIGATION_WIDTH}px`,
        backgroundColor: "transparent",
        boxShadow: "none",
      };

  const contentStyle = isXs
    ? { width: "100%" }
    : {
        position: "relative" as const,
        height: "100%",
        backgroundColor: "transparent",
      };

  const innerStyle = isXs
    ? { width: "100%" }
    : {
        position: "absolute" as const,
        right: 0,
        width: "100%",
        maxWidth: `${MAX_FEEDBACK_CONTAINER_WIDTH - FEEDBACK_NAVIGATION_WIDTH}px`,
        zIndex: 100,
      };

  return (
    <Drawer isOpen={true} lazy={true} onClose={onClose} style={drawerStyle}>
      <Box css={contentStyle}>
        <Box
          className={!isXs ? "bls" : undefined}
          backgroundColor="#FFF"
          flex={1}
          css={innerStyle}
          minHeight="100%"
        >
          <Box
            fontSize={24}
            fontWeight={600}
            p="24px 16px 16px 16px"
            backgroundColor="white"
            style={{
              position: "relative",
              borderBottom: `1px solid ${LIGHT_GRAY}`,
            }}
          >
            Feedback
          </Box>
          <Box p={3} className="bbs">
            <ChatMessage {...selectedMessage} isFeedbackActive={true} />
          </Box>
          {aiComments.length > 0 && (
            <Box p={3} className="bbs">
              <RoleplaySessionComments comments={aiComments} title="Summary" />
            </Box>
          )}
          <Box p={3}>
            {selectedMessage.evaluatedCriterion && (
              <RoleplaySessionDetailFeedback
                key={selectedMessage.id}
                evaluatedCriterion={ensureNonNullArray(
                  selectedMessage.evaluatedCriterion,
                )}
                defaultOpen={aiComments.length > 0 ? false : true}
              />
            )}
          </Box>
          <FeedbackNavigationButtons
            onClose={onClose}
            onNavigate={onNavigate}
            isXs={isXs}
          />
        </Box>
      </Box>
    </Drawer>
  );
}

export function RoleplayFeedbacks({
  selectedMessage,
  onClose,
  onNavigate,
}: RoleplayFeedbacksProps) {
  const isXxl = useMediaQuery(FEEDBACK_MEDIA_QUERY);
  const aiComments = ensureNonNullArray(selectedMessage.aiComments);
  const location = useLocation();

  useEffect(() => {
    if (!location.pathname.includes("transcript")) {
      onClose();
    }
  }, [location]);

  if (!selectedMessage?.feedbacks?.length) return null;

  if (!isXxl) {
    return (
      <DrawerFeedback
        selectedMessage={selectedMessage}
        onClose={onClose}
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <Flex
      flexDirection="column"
      className="bls"
      backgroundColor="#FFF"
      flex={1}
      css={{
        position: "sticky",
        top: STANDARD_NAV_HEIGHT,
        right: `0px`,
        maxWidth: MAX_FEEDBACK_CONTAINER_WIDTH,
        zIndex: 100,
        width: `calc(100% - ${SIDE_NAV_WIDTH}px)`,
      }}
      maxHeight="95vh"
      height="95vh"
    >
      <Box
        style={{
          position: "sticky",
          top: "65px",
        }}
      >
        <Box
          fontSize={24}
          fontWeight={600}
          p="24px 16px 16px 16px"
          backgroundColor="white"
          style={{
            position: "relative",
            borderBottom: `1px solid ${LIGHT_GRAY}`,
          }}
        >
          Feedback
          <FeedbackNavigationButtons
            onClose={onClose}
            onNavigate={onNavigate}
          />
        </Box>
      </Box>
      <Flex flex={1} flexDirection="column" overflow="auto">
        {aiComments.length > 0 && (
          <Box
            p={3}
            style={{
              borderBottom: `1px solid ${LIGHT_GRAY}`,
            }}
          >
            <RoleplaySessionComments comments={aiComments} title="Summary" />
          </Box>
        )}

        <Box
          p={3}
          style={{
            borderBottom: `1px solid ${LIGHT_GRAY}`,
          }}
        >
          <RoleplaySessionDetailFeedback
            key={selectedMessage.id}
            evaluatedCriterion={ensureNonNullArray(
              selectedMessage.evaluatedCriterion,
            )}
            defaultOpen={aiComments.length > 0 ? false : true}
          />
        </Box>
      </Flex>
    </Flex>
  );
}
